<template>
  <Head>
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <meta name="apple-mobile-web-app-capable" content="yes">
    <meta name="mobile-web-app-capable" content="yes">
    <meta name="twitter:card" content="summary_large_image">
    <!-- <meta name="twitter:site" content="@TODO_CHANGEME">
    <meta name="twitter:creator" content="@TODO_CHANGEME"> -->
    <meta property="og:type" content="website">
    <meta property="og:image:type" content="image/jpeg">
    <meta property="og:image:width" content="1200">
    <meta property="og:image:height" content="675">
  </Head>

  <div class="relative h-[100vh]" :style="{height: `${windowHeight}px`}">
    <transition mode="out-in">
      <div v-if="isLandscape && ((mobile && ios) || (mobile && !ios && !tablet))" class="isLandscape fixed w-full h-full z-3 flex flex-col items-center justify-center">
        <SectionBg theme="purple" />
        <TPCLogo class="logo relative mb-9" />
        <RotateDevice class="rotate-device c-gold relative mb-3" />
        <p class="rotate-label label text-center c-white uppercase  relative">{{ $t('Rotate your device') }}</p>
      </div>
    </transition>
    <router-view v-slot="{ Component, route }">
      <transition mode="out-in">
        <!-- be careful with this key: it will transition all pages with different route names (not full paths) -->
        <component :is="Component" v-if="!route.metadata?.loadingChunk && Component" :key="route.name" />
      </transition>
    </router-view>

    <!-- version info -->
    <VersionInfo v-if="stage !== 'production'" />
  </div>
</template>

<script>
import { inject, computed, reactive, defineComponent } from 'vue'
import { useRoute } from 'vue-router'
import { Head, useHead } from '@vueuse/head'
import { useMediaQuery, useWindowSize } from '@vueuse/core'
import {
  getGlobalFeatures,
  useI18nInjection,
  useModernizr,
  usePlatform,
  useModernizrStyleClasses,
  useSeo,
  VersionInfo
} from '@monogrid/vue-boost'
import Spinner from 'vue-easy-spinner/package/Spinner.vue'

import { preloaderInjection, env } from './main'

import SectionBg from '@/molecules/SectionBg.vue'

import shareImageTODOChangeMe from '@/assets/share.jpg'
import RotateDevice from '@/assets/svg/rotate-device.svg'
import TPCLogo from '@/assets/svg/tpc-logo.svg'

export default defineComponent({
  name: 'App',
  components: {
    Head,
    Spinner,
    SectionBg,
    VersionInfo,
    RotateDevice,
    TPCLogo
  },
  /**
   * Introduction
   *
   * https://v3.vuejs.org/guide/composition-api-introduction.html#basics-of-composition-api
   *
   * Setup functions
   * https://v3.vuejs.org/guide/composition-api-setup.html
   */
  setup (props, { attrs, slots, emit }) {
    const { setSeoDefaults, getReactiveSeo } = useSeo()
    const seo = getReactiveSeo()
    // https://next.router.vuejs.org/guide/advanced/composition-api.html
    const route = useRoute()

    const platform = usePlatform()
    const isLandscape = useMediaQuery('(orientation:landscape)')

    const { USE_I18N } = getGlobalFeatures()
    let i18n
    if (USE_I18N) {
      // https://vue-i18n.intlify.dev/guide/advanced/composition.html#basic-usage
      i18n = inject(useI18nInjection)()
    }

    const preloader = inject(preloaderInjection)
    if (preloader) {
      preloader.visible = false
    }

    const testClasses = useModernizrStyleClasses({ ...useModernizr(), ...usePlatform() })

    setSeoDefaults({
      siteTitle: 'The Party Collection 2022 | Valentino',
      image: `https://${env.SERVICE_TARGET_DOMAIN}${shareImageTODOChangeMe}`
    })

    useHead({
      title: seo.title,
      htmlAttrs: USE_I18N
        ? reactive({ lang: i18n.locale, class: testClasses })
        : reactive({ lang: 'en', class: testClasses }),
      meta: [
        { key: 'ogtitle', property: 'og:title', itemprop: 'name', content: seo.title },
        { key: 'description', name: 'description', content: seo.description },
        { key: 'ogdescription', property: 'og:description', content: seo.description },
        { key: 'ogimage', property: 'og:image', content: seo.image },
        { key: 'ogurl', property: 'og:url', content: computed(() => `https://${env.SERVICE_TARGET_DOMAIN}${route.fullPath}`) }
      ]
    })
    const { height: windowHeight } = useWindowSize()
    return {
      stage: env.SERVICE_STAGE,
      windowHeight,
      isLandscape,
      mobile: platform.mobile,
      tablet: platform.tablet,
      ios: platform.ios
    }
  }
})
</script>

<style lang="scss">
@font-face {
  font-family: BodoniModa;
  font-weight: 400;
  src:
    url("@/assets/fonts/bodonimoda/BodoniModa-Regular.woff2") format("woff2"),
    url("@/assets/fonts/bodonimoda/BodoniModa-Regular.woff") format("woff");
}

@font-face {
  font-family: BodoniModa;
  font-style: italic;
  font-weight: 400;
  src:
    url("@/assets/fonts/bodonimoda/BodoniModa-Italic.woff2") format("woff2"),
    url("@/assets/fonts/bodonimoda/BodoniModa-Italic.woff") format("woff");
}

@font-face {
  font-family: DINPro;
  font-style: normal;
  font-weight: 400;
  src:
    url("@/assets/fonts/dinpro/DINPro-Regular.woff2") format("woff2"),
    url("@/assets/fonts/dinpro/DINPro-Regular.woff") format("woff");
}

@font-face {
  font-family: DINPro;
  font-style: normal;
  font-weight: 500;
  src:
    url("@/assets/fonts/dinpro/DINPro-Medium.woff2") format("woff2"),
    url("@/assets/fonts/dinpro/DINPro-Medium.woff") format("woff");
}

html,
body {
  overflow: hidden;
  overscroll-behavior: none;
  touch-action: pan-x;
}

.loading-page-chunk {
  text-align: center;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.h-\[100vh\] {
  max-height: 100svh;
}

// fonts
.h1 {
  font-size: vh(50px, $design-height-mobile);

  @screen lg {
    font-size: vh(110px, $design-height-desktop);
    line-height: 1.1;
  }
}

.h2 {
  font-size: vh(48px, $design-height-mobile);
  line-height: 1.1;

  @screen lg {
    font-size: vh(70px, $design-height-desktop);
  }
}

.h3 {
  font-size: vh(30px, $design-height-mobile);
  line-height: 1.2;

  @screen lg {
    font-size: vh(44px, $design-height-desktop);
  }
}

.h4 {
  font-size: vh(26px, $design-height-mobile);
  line-height: 1.2;

  @screen lg {
    font-size: vh(42px, $design-height-desktop);
    line-height: 1.1;
  }
}

.h5 {
  font-size: vh(26px, $design-height-mobile);
  line-height: 1.1;

  @screen lg {
    font-size: vh(26px, $design-height-desktop);
  }
}

.h6 {
  font-size: vh(16px, $design-height-mobile);
  line-height: 1.3;

  @screen lg {
    font-size: max(rem(18px), vh(18px, $design-height-desktop));
  }
}

.h1,
.h2,
.h3,
.h4,
.h5,
.h6,
.p {
  font-family: BodoniModa, serif;
  font-weight: 400;
  letter-spacing: -0.03em;
}

.h1,
.h2,
.h3,
.h4,
.h5,
.h6 {
  @include fix-bodoni;
}

.cta-label {
  font-family: BodoniModa, serif;
  font-size: max(12px, vh(12px, $design-height-mobile));
  font-weight: 400;

  @screen lg {
    font-size: max(rem(14px), vh(14px, $design-height-desktop));
    letter-spacing: -0.03em;
    line-height: 1.05;
  }
}

.p {
  font-size: max(14px, vh(14px, $design-height-mobile));
  font-weight: 400;
  line-height: 1.28;

  @screen lg {
    font-size: max(rem(16px), vh(16px, $design-height-desktop));
  }
}

.label {
  font-family: DINPro, sans-serif;
  font-size: max(rem(12px), vh(12px, $design-height-mobile));
  font-weight: 500;
  letter-spacing: 0.09em;
  line-height: 1;

  @screen lg {
    font-size: max(rem(12px), vh(12px, $design-height-desktop));
  }
}

.image-shadow {
  filter: drop-shadow(rem(5px 7px 11px) rgba(0, 0, 0, 13%));
}

.section-padded {
  // stylelint-disable-next-line
  padding: vh($safe-area-mobile-top $safe-area-mobile-right $safe-area-mobile-bottom $safe-area-mobile-left, $design-height-mobile);

  @screen lg {
    // stylelint-disable-next-line
    padding-top: max($safe-area-desktop-top, vh($safe-area-desktop-top, $design-height-desktop));
    // stylelint-disable-next-line
    padding-right: max($safe-area-desktop-right, vh($safe-area-desktop-right, $design-height-desktop));
    // stylelint-disable-next-line
    padding-bottom: max($safe-area-desktop-bottom, vh($safe-area-desktop-bottom, $design-height-desktop));
    // stylelint-disable-next-line
    padding-left: max($safe-area-desktop-left, vh($safe-area-desktop-left, $design-height-desktop));
  }
}

ul.swiper-wrapper {
  padding: 0;
  margin: 0;

  .swiper-slide {
    padding: 0;
    margin: 0;
  }
}

.isLandscape {
  padding: rem(100px, 230px);
}

.logo {
  height: rem(76px);
}

.rotate-device {
  height: rem(22px);
}

.rotate-label {
  width: rem(140px);
}

// colors
.c-transition {
  transition: color 0.3s ease-out;
}

.c-black {
  color: $c-black;
}

.c-white {
  color: $c-white;
}

.c-purple {
  color: $c-purple;
}

.c-purple-light {
  color: $c-purple-light;
}

.c-gold {
  color: $c-gold;
}

.c-linear-gradient-gold {
  color: $c-linear-gradient-gold;
}

[data-parallax] {
  will-change: transform;
}

</style>
